import { computed } from 'vue'
import { schoolClassGroupFragment } from '/@/common/school/api/fragments'
import type { SchoolClassGroup } from '/@/common/school/types'
import { useMutation, useQuery } from '/@/core/graphql'
import { gql } from '@apollo/client/core'

interface TaskStatusInput {
  designerTaskId: string
  designerGoalId: string
  isCompleted: boolean
}

interface ClassStatusInput {
  designerClassId: string
  designerGoalId: string
  isCompleted: boolean
}

export function useSchoolApi() {
  const updateTaskStatus = useMutation(gql`
    mutation updateDesignerTaskStatus($input: DesignerTaskStatusInput!) {
      designer {
        goal {
          updateTaskStatus(input: $input) {
            record {
              id
              completedAt
              task {
                id
                goal {
                  id
                  designerInfo {
                    id
                    status
                    completedAt
                  }
                }
              }
            }
          }
        }
      }
    }
  `)

  const updateClassStatus = useMutation(gql`
    mutation updateDesignerClassStatus($input: DesignerClassStatusInput!) {
      designer {
        goal {
          updateClassStatus(input: $input) {
            record {
              id
              completedAt
              class {
                id
                goal {
                  id
                  designerInfo {
                    id
                    status
                    completedAt
                  }
                }
              }
            }
          }
        }
      }
    }
  `)

  return {
    async updateTaskStatus(input: TaskStatusInput) {
      await updateTaskStatus.mutate({ input })
    },
    async updateClassStatus(input: ClassStatusInput) {
      await updateClassStatus.mutate({ input })
    },
  }
}

export function useSchoolData() {
  const { result, loading } = useQuery<{
    school: { groups: SchoolClassGroup[] }
  }>(gql`
    ${schoolClassGroupFragment}
    query getSchoolGroups {
      school {
        groups {
          ...SchoolClassGroupProps
        }
      }
    }
  `)

  const groups = computed(() => result.value?.school?.groups ?? [])

  return { groups, loading }
}
